import {
  AfterViewInit,
  ApplicationRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  ListTableFiltersWrapperParent
} from '@app/wrappers/pages/list-table-filters-wrapper/list-table-filters-wrapper-parent';
import { AnomalyPolygonsService} from '@6grain/masofa-monolith-api-client/satellite-indices';
import {IMsfFormBuilder} from "@components/form-builder/form-builder";
import {ButtonDirective} from 'primeng/button';
import {DatePipe} from '@angular/common';
import {ServerTranslatePipe} from '@app/utils/pipes/server-translate-pipe';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {Tooltip} from 'primeng/tooltip';
import {TableColumn} from '@components/data-table/data-table.component';
import {Router, RouterLink} from '@angular/router';
import {DateFormatLangPipe} from '@app/utils/pipes/date-format-lang-pipe';
import {
  ListTableWithoutFiltersWrapper
} from '@app/wrappers/pages/list-table-without-filters-wrapper/list-table-without-filters-wrapper';
import {CommonModule} from '@angular/common';
import {DynamicDataTableComponent} from '@components/dynamic-data-table/dynamic-data-table.component';
import {SortType} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/sortType';
import {DialogService} from 'primeng/dynamicdialog';
import {Bid, SeasonService} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {BidService} from '@6grain/masofa-monolith-api-client/crop-monitoring/api/bid.service';
import {BidWorkTypeService} from '@6grain/masofa-monolith-api-client/dictionaries/api/bidWorkType.service';
import {Crop, CropService} from '@6grain/masofa-monolith-api-client/dictionaries';
import {catchError, map, Observable, of} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {FilterOperator} from '@app/utils/filter-operator';
import {
  CreateBidFormComponent
} from '@app/pages/electronic-field-map/pages/fields/passport/forms/create-bid-form/create-bid-form.component';
import {BidFormData} from '@app/pages/electronic-field-map/pages/fields/passport/forms/create-bid-form/bid-form-data';
import {
  buildBidWorkTypeFormField,
  BID_WORK_TYPE_IDS,
} from '@shared/data/bid-work-type.util';
import {
  resolveAnomalyPolygonColor,
  translateKeyForAnomalyType,
  translateKeyForProductSource,
} from '@app/pages/crop-monitoring/pages/anomalies/anomaly-type-colors';
import {RegionDisplay} from '@components/region-display/region-display';
import {PolygonConverterUtil} from '@app/utils/polygon-converter.util';

function contrastTextForHexBackground(hex: string): '#ffffff' | '#111827' {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) {
    return '#ffffff';
  }
  const r = parseInt(m[1], 16) / 255;
  const g = parseInt(m[2], 16) / 255;
  const b = parseInt(m[3], 16) / 255;
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.62 ? '#111827' : '#ffffff';
}

@Component({
  selector: 'app-anomalies-list',
  standalone: true,
  templateUrl: './anomalies-list.html',
  imports: [
    CommonModule,
    ButtonDirective,
    ServerTranslatePipe,
    TranslatePipe,
    Tooltip,
    DateFormatLangPipe,
    ListTableWithoutFiltersWrapper,
    DynamicDataTableComponent,
    RegionDisplay,
    RouterLink,
  ],
  // DynamicDialog for create-bid flow above map dialogs (appendTo body).
  providers: [DatePipe, DialogService],
  styleUrl: './anomalies-list.css'
})
export class AnomaliesList extends ListTableFiltersWrapperParent<any, AnomalyPolygonsService> implements OnInit, AfterViewInit {
  override service = inject(AnomalyPolygonsService);
  override getAllMethod = 'satelliteAnomalyPolygonsCustomGetAnomalyPolygonsPost';
  override getTotalMethod = 'satelliteAnomalyPolygonsGetTotalCountPost';
  override storageKey = '';
  override filtersForms: IMsfFormBuilder[] = [];
  readonly tableColumns = signal<TableColumn[]>([]);
  /** Deferred until {@link ngAfterViewInit} so `#regionTemplate` / etc. exist (incompatible with `@if` + early ViewChild). */
  readonly columnsReady = signal(false);

  @ViewChild('actionsTemplate') actionsTemplate!: TemplateRef<unknown>;
  @ViewChild('regionTemplate') regionTemplate!: TemplateRef<unknown>;
  @ViewChild('cropTemplate') cropTemplate!: TemplateRef<unknown>;
  @ViewChild('anomalyTypeTemplate') anomalyTypeTemplate!: TemplateRef<unknown>;
  @ViewChild('productSourceTemplate') productSourceTemplate!: TemplateRef<unknown>;
  @ViewChild('anomaliesTemplate') anomaliesTemplate!: TemplateRef<unknown>;
  @ViewChild('requestNumberTemplate') requestNumberTemplate!: TemplateRef<unknown>;

  @Output() onZoomToPoint = new EventEmitter<any>();

  @Input() compactLayout = false;

  @Input() set appliedFilters(value: Object[] | null | undefined) {
    if (value === undefined || value === null) {
      return;
    }
    this.filtersParams = [...value];
    this.toggleFilter = !this.toggleFilter;
  }

  private readonly router = inject(Router);
  translate = inject(TranslateService);
  private readonly dialogService = inject(DialogService);
  private readonly bidService = inject(BidService);
  private readonly bidWorkTypeService = inject(BidWorkTypeService);
  private readonly seasonService = inject(SeasonService);
  private readonly cropService = inject(CropService);
  private readonly appRef = inject(ApplicationRef);

  readonly defaultTableParams = {
    take: 10,
    offset: 0,
    sortBy: 'originalDate',
    sort: SortType.NUMBER_1,
    filters: [] as any[],
  };

  override ngOnInit(): void {
    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    this.initTableColumns();
    this.columnsReady.set(true);
  }

  private initTableColumns(): void {
    this.filtersForms = [];
    this.tableColumns.set([
      {
        field: 'originalDate',
        header: 'Detection date',
        sortable: false,
        type: 'custom',
        customTemplate: this.anomaliesTemplate,
      },
      {
        field: 'fieldName',
        header: 'ANOMALIES_TABLE_FIELD',
        type: 'text',
        sortable: false,
      },
      {
        field: 'cropName',
        header: 'ANOMALIES_TABLE_CROP',
        type: 'custom',
        customTemplate: this.cropTemplate,
      },
      {
        field: '',
        header: 'ANOMALIES_TABLE_SOURCE',
        sortable: false,
        type: 'custom',
        customTemplate: this.productSourceTemplate,
        minWidth: '148px',
      },
      {
        field: '',
        header: 'ANOMALIES_TABLE_REGION',
        sortable: false,
        type: 'custom',
        customTemplate: this.regionTemplate,
      },
      {
        field: '',
        header: 'ANOMALIES_TABLE_TYPE',
        sortable: false,
        type: 'custom',
        customTemplate: this.anomalyTypeTemplate,
      },
      {
        field: '',
        header: 'Request number',
        sortable: false,
        type: 'custom',
        customTemplate: this.requestNumberTemplate,
        minWidth: '100px',
      },
      {
        field: '',
        header: '',
        type: 'custom',
        customTemplate: this.actionsTemplate,
      },
    ]);
  }

  anomalyTypeCell(row: unknown): string {
    const ap = this.anomalyRowPayload(row);
    const key = translateKeyForAnomalyType(ap?.['anomalyType'] ?? ap?.['type']);
    return key ? this.translate.instant(key) : '—';
  }

  anomalyTypeBadgeStyle(row: unknown): Record<string, string> {
    const bg = resolveAnomalyPolygonColor(this.anomalyRowPayload(row));
    return {
      backgroundColor: bg,
      color: contrastTextForHexBackground(bg),
    };
  }

  anomalyRegionId(row: unknown): string | null {
    const ap = this.anomalyRowPayload(row);
    const top = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
    const nestedRegion = ap?.['region'] && typeof ap['region'] === 'object'
      ? (ap['region'] as Record<string, unknown>)?.['id']
      : undefined;
    const topRegion = top['region'] && typeof top['region'] === 'object'
      ? (top['region'] as Record<string, unknown>)?.['id']
      : undefined;
    const raw = ap?.['regionId'] ?? nestedRegion ?? top['regionId'] ?? topRegion;
    if (raw == null || raw === '') {
      return null;
    }
    return String(raw);
  }

  productSourceCell(row: unknown): string {
    const ap = this.anomalyRowPayload(row);
    const key = translateKeyForProductSource(
      ap?.['productSourceType'] ?? ap?.['source'] ?? ap?.['productSource'],
    );
    return key ? this.translate.instant(key) : '—';
  }

  /** Prefixed phrase uses {@link ANOMALIES_BID_MONITORING_OF_ANOMALY}; type label uses existing {@code ANOMALY_TYPE_*} keys. */
  private buildBidDescriptionFromAnomaly(row: unknown): string {
    const prefix = this.translate.instant('ANOMALIES_BID_MONITORING_OF_ANOMALY');
    const typeLabel = this.anomalyTypeCell(row).trim();
    const typePart = typeLabel && typeLabel !== '—' ? typeLabel : '';
    return typePart ? `${prefix}: ${typePart}` : prefix;
  }

  private anomalyTypeNumeric(row: unknown): number | undefined {
    const ap = this.anomalyRowPayload(row);
    const raw = ap?.['anomalyType'] ?? ap?.['type'];
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }

  private anomalyRowPayload(row: unknown): Record<string, unknown> | undefined {
    if (!row || typeof row !== 'object') {
      return undefined;
    }
    const r = row as Record<string, unknown>;
    const nested = r['anomalyPolygon'];
    if (nested && typeof nested === 'object') {
      return nested as Record<string, unknown>;
    }
    return r;
  }

  private anomalyPolygonCenter(row: any): {lat: number; lng: number} | null {
    const polygonJson = row?.anomalyPolygon?.polygonJson;
    const coordinates = PolygonConverterUtil.polygonJsonToCoordinates(polygonJson);
    if (!coordinates?.length) {
      return null;
    }
    return PolygonConverterUtil.getPolygonCenter(coordinates);
  }

  openEditModal(row: any): void {
    this.router.navigate([`/electronic-field-map/fields/${row?.anomalyPolygon?.fieldId}/general`]);
  }

  /** Crops shown in the Create request modal: table crop when present, else all active field crops. */
  private loadCropsForCreateBid(fieldId: string, tableCropId?: string | null): Observable<Crop[]> {
    if (tableCropId) {
      return this.cropService.dictionariesCropGetByIdIdGet(String(tableCropId)).pipe(
        map((crop) => (crop ? [crop as Crop] : [])),
        catchError(() => this.loadActiveFieldCrops(fieldId)),
      );
    }
    return this.loadActiveFieldCrops(fieldId);
  }

  private loadActiveFieldCrops(fieldId: string): Observable<Crop[]> {
    return this.seasonService.cropMonitoringSeasonGetActiveSeasonByDateGet(fieldId).pipe(
      switchMap((seasons) => {
        const cropIds = [...new Set((seasons ?? []).map((s) => s.cropId).filter(Boolean))] as string[];
        if (!cropIds.length) {
          return of([] as Crop[]);
        }
        return this.cropService.dictionariesCropGetByQueryPost({
          filters: [
            {
              filterField: 'id',
              filterOperator: FilterOperator.AnyOf,
              filterValue: cropIds,
            },
          ],
        } as any).pipe(catchError(() => of([] as Crop[])));
      }),
      catchError(() => of([] as Crop[])),
    );
  }

  private getCreateBidFormFields(cropHolder: {list: Crop[]}): IMsfFormBuilder[] {
    return [
      {
        name: 'cropId',
        title: 'Culture',
        type: 'select',
        placeholder: 'Select crop',
        getSelectOptions: () => cropHolder.list,
      },
      {
        name: 'deadlineDate',
        title: 'Deadline',
        type: 'date',
        placeholder: 'Select date',
      },
      {
        name: 'customer',
        title: 'Customer',
        type: 'input',
      },
      {
        name: 'description',
        title: 'Description',
        type: 'textarea',
      },
      buildBidWorkTypeFormField(this.bidWorkTypeService),
    ];
  }

  openCreateBidDialog(row: any, event?: Event): void {
    event?.stopPropagation();
    const rawFieldId = row?.anomalyPolygon?.fieldId;
    const fieldId = rawFieldId != null && rawFieldId !== '' ? String(rawFieldId) : '';
    if (!fieldId) {
      return;
    }


    const description = this.buildBidDescriptionFromAnomaly(row);
    const anomalyTypeNum = this.anomalyTypeNumeric(row);
    const anomalyCropId = row?.anomalyPolygon?.cropId ?? row?.cropId;
    const anomalyPolygonId = row?.anomalyPolygon?.id ?? row?.anomalyPolygonId ?? null;
    const center = this.anomalyPolygonCenter(row);
    const bid: BidFormData & {anomalyType?: number; anomalyPolygonId?: string | null} = {
      fieldId,
      cropId: (anomalyCropId ?? null) as any,
      deadlineDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      description,
      lat: center?.lat as any,
      lng: center?.lng as any,
      anomalyPolygonId: anomalyPolygonId != null ? String(anomalyPolygonId) : null,
      bidWorkType: BID_WORK_TYPE_IDS.Default,
    };

    if (anomalyTypeNum !== undefined) {
      bid.anomalyType = anomalyTypeNum;
    }

    const cropHolder = {list: [] as Crop[]};
    const fields = this.getCreateBidFormFields(cropHolder);
    this.openCreateBidFormModal(bid, fields);

    this.loadCropsForCreateBid(fieldId, anomalyCropId ?? null)
      .pipe(catchError(() => of([] as Crop[])))
      .subscribe((crops) => {
        cropHolder.list = [...crops];
        this.appRef.tick();
      });
  }

  private openCreateBidFormModal(bid: Bid, fields: IMsfFormBuilder[]): void {
    const dialogRef = this.dialogService.open(CreateBidFormComponent, {
      header: this.translate.instant('Create request'),
      width: '40%',
      closable: true,
      modal: true,
      appendTo: 'body',
      duplicate: true,
      baseZIndex: 12000,
      data: {
        model: bid,
        serviceMethod: 'cropMonitoringBidCustomCreatePost',
        service: this.bidService,
        fields,
      },
    });

    dialogRef?.onClose?.subscribe((result: {success?: boolean} | undefined) => {
      if (result?.success) {
        this.toggleFilter = !this.toggleFilter;
      }
    });
  }
}
